import { Box } from "@mui/system";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Autocomplete, Button, Stack, TextField } from "@vapor/react-material";
import { debounce } from "lodash";
import type { SyntheticEvent } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { JSX } from "react/jsx-runtime";

import type {
    FulfilmentDTO,
    TaxPayer,
} from "../../../../../core/api/use-get-tax-payers/FulfilmentDTO";
import type {
    GovernmentAgencyType,
    TaxSubmissionDetailDTO,
} from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { TaxSubmissionDetailTaxPayersTable } from "../tax-submission-detail-tax-payers-table/TaxSubmissionDetailTaxPayersTable";
import { TaxSubmissionDetailToolbar } from "../tax-submission-detail-toolbar/TaxSubmissionDetailToolbar";

interface TaxSubmissionDetailProps {
    loading: boolean;
    onClickPreviewOutcome: (
        availablesOutcomeTypes: string[],
        governmentAgencyType: GovernmentAgencyType,
        taxSubmissionId: string,
        selectedOutcomeType: string,
        taxPayer?: string,
    ) => void;
    onClickF24Preview: (
        taxSubmissionId: string,
        taxPayerId: string,
        taxPayerName: string,
        taxPayerPosition: number,
        delegationPosition?: number,
    ) => void;
    onTaxSubmissionUpdate: () => void;
    revokeTaxPayers?: boolean;
    taxSubmissionDetail: TaxSubmissionDetailDTO;
}

export const TaxSubmissionDetail: React.FC<TaxSubmissionDetailProps> = ({
    loading,
    onClickPreviewOutcome,
    onClickF24Preview,
    onTaxSubmissionUpdate,
    revokeTaxPayers = false,
    taxSubmissionDetail,
}): JSX.Element => {
    const { t } = useTranslation();

    const [revokeTaxSubmissionsEnabled, setRevokeTaxSubmissionsEnabled] =
        useState(revokeTaxPayers);
    const [selectedTaxPayer, setSelectedTaxPayer] = useState<TaxPayer | null>(
        null,
    );
    const [selectedFulfilments, setSelectedFulfilments] = useState<
        FulfilmentDTO[]
    >([]);

    const filteredTaxSubmissionDetail = useMemo(() => {
        if (!selectedTaxPayer) return taxSubmissionDetail;
        return {
            ...taxSubmissionDetail,
            fulfilments: taxSubmissionDetail.fulfilments.filter(
                (fulfilment) =>
                    fulfilment.taxpayer.taxId === selectedTaxPayer.taxId,
            ),
        };
    }, [selectedTaxPayer, taxSubmissionDetail]);

    const taxPayerOptions = useMemo(
        () => [
            ...new Set(
                taxSubmissionDetail.fulfilments.map(
                    (fulfilment) => fulfilment.taxpayer,
                ),
            ),
        ],
        [taxSubmissionDetail],
    );

    const taxPayersToRevoke: { protocol: string; taxId: string }[] =
        useMemo(() => {
            return selectedFulfilments
                .filter((f) => !!f.protocol)
                .map((fulfilment) => {
                    return {
                        protocol: fulfilment.protocol as string, // it's safe because we filter protocols above
                        taxId: fulfilment.taxpayer.taxId,
                    };
                });
        }, [selectedFulfilments]);

    const debouncedHandleInputChange = debounce(
        (_: SyntheticEvent, value: string) => {
            if (!value) setSelectedTaxPayer(null);
        },
        300,
    );

    const handleTaxPayerChange = (
        _: SyntheticEvent<Element, Event>,
        value: TaxPayer,
    ) => {
        setSelectedTaxPayer(value);
    };

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ placeItems: "center" }}
            >
                <Autocomplete
                    disableClearable={false}
                    getOptionLabel={(option: TaxPayer) => option.name}
                    loading={loading}
                    isOptionEqualToValue={(option, value) => {
                        return option.taxId === value.taxId;
                    }}
                    noOptionsText={t(
                        "views.home.components.tax_submissions_detail_filters.taxPayer.no_options_text",
                    )}
                    onChange={handleTaxPayerChange}
                    onInputChange={debouncedHandleInputChange}
                    options={taxPayerOptions}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t(
                                "views.home.components.tax_submissions_detail_filters.taxPayer.label",
                            )}
                            InputLabelProps={{
                                ...params.InputLabelProps,
                                shrink: true,
                            }}
                            InputProps={{
                                ...params.InputProps,
                                sx: { width: "12vw" },
                            }}
                            placeholder={t(
                                "views.home.components.tax_submissions_detail_filters.taxPayer.placeholder",
                            )}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    padding: 0,
                                    paddingLeft: "4px",
                                    width: "250px",
                                },
                            }}
                        />
                    )}
                    value={selectedTaxPayer}
                />
                {taxSubmissionDetail.isRevocable && (
                    <Box>
                        <Button
                            onClick={() =>
                                setRevokeTaxSubmissionsEnabled(
                                    (oldValue) => !oldValue,
                                )
                            }
                            variant="text"
                        >
                            {revokeTaxSubmissionsEnabled
                                ? t(
                                      "views.home.components.tax_submission_detail.revoke_taxSubmission.btn.cancel",
                                  )
                                : t(
                                      "views.home.components.tax_submission_detail.revoke_taxSubmission.btn.revoke",
                                  )}
                        </Button>
                    </Box>
                )}
            </Stack>
            {taxSubmissionDetail.isRevocable && revokeTaxSubmissionsEnabled && (
                <Box p={2} sx={{ background: "#FEEDC9", borderRadius: "8px" }}>
                    <Box>
                        <Typography variant="body700">
                            {t(
                                "views.home.components.tax_submission_detail.revoke_taxSubmission.btn.revoke",
                            )}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body">
                            {t(
                                "views.home.components.tax_submission_detail.revoke_taxSubmission.alert.message",
                            )}
                        </Typography>
                    </Box>
                </Box>
            )}
            <TaxSubmissionDetailTaxPayersTable
                loading={loading}
                onClickPreviewOutcome={onClickPreviewOutcome}
                onClickF24Preview={onClickF24Preview}
                onRowSelectionModelChange={(
                    selectedFulfilmentIds: string[],
                ) => {
                    setSelectedFulfilments((prev) => {
                        // select/deselect all the fulfilments with the same taxId and protocol
                        const getTaxPayerProtocolKey = (f: FulfilmentDTO) =>
                            `${f.taxpayer.taxId}_${f.protocol}`;

                        const deselectedTaxPayerAndProtocol = new Set(
                            prev
                                .filter(
                                    (f) =>
                                        !selectedFulfilmentIds.includes(f.id),
                                )
                                .map(getTaxPayerProtocolKey),
                        );

                        const selectedTaxPayerAndProtocol = new Set(
                            selectedFulfilmentIds.map((id) => {
                                const fulfilment =
                                    taxSubmissionDetail.fulfilments.find(
                                        (f) => f.id === id,
                                    );
                                return (
                                    fulfilment &&
                                    getTaxPayerProtocolKey(fulfilment)
                                );
                            }),
                        );

                        return taxSubmissionDetail.fulfilments.filter(
                            (fulfilment) =>
                                !!fulfilment.isRevocable &&
                                selectedTaxPayerAndProtocol.has(
                                    getTaxPayerProtocolKey(fulfilment),
                                ) &&
                                !deselectedTaxPayerAndProtocol.has(
                                    getTaxPayerProtocolKey(fulfilment),
                                ),
                        );
                    });
                }}
                revokeTaxSubmissionsEnabled={revokeTaxSubmissionsEnabled}
                selectedFulfilmentIds={selectedFulfilments.map((f) => f.id)}
                taxSubmissionDetail={filteredTaxSubmissionDetail}
            />
            <Box sx={{ ml: "-32px !important" }}>
                <TaxSubmissionDetailToolbar
                    onClickPreviewOutcome={onClickPreviewOutcome}
                    onTaxSubmissionUpdate={onTaxSubmissionUpdate}
                    revokeTaxSubmissionsEnabled={revokeTaxSubmissionsEnabled}
                    taxPayersToRevoke={taxPayersToRevoke}
                    taxSubmissionDetail={taxSubmissionDetail}
                />
            </Box>
        </Stack>
    );
};
